import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useFilterStore } from "@/marketing/stores/useFilterStore";
import { useListStore } from "@/marketing/stores/useListStore";
import { useSelectionStore } from "@/marketing/stores/useSelectionStore";
import { INFLUENCER_QUERIES } from "@/marketing/infrastructure/influencer/utils/queries";
import { INFLUENCER_KEYS } from "@/marketing/infrastructure/influencer/utils/keys";
import {
  influencerAPIClient,
  type ExportProgress,
} from "@/marketing/infrastructure/influencer/influencerAPIClient";
import type { Influencer } from "@/marketing/models/influencer";
import { FilterBar } from "./filters/FilterBar";
import { InfluencerTable } from "./InfluencerTable";
import { InfluencerTablePagination } from "./InfluencerTablePagination";
import { InfluencerProfileModal } from "./InfluencerProfileModal";
import { BulkActionBar } from "./BulkActionBar";
import { SelectAllBanner } from "./SelectAllBanner";
import { ExportButton } from "./ExportButton";
import { ImportDialog } from "./ImportDialog";
import { TabBar } from "./TabBar";

const SELECTED_PAGE_SIZE = 100;

export function InfluencerSearchPage() {
  const filters = useFilterStore((s) => s.filters);
  const pagination = useFilterStore((s) => s.pagination);
  const platformHeaderLabel = useMemo(() => {
    if (filters.platform === "tiktok") return "TikTok";
    if (filters.platform === "instagram") return "Instagram";
    return "creator";
  }, [filters.platform]);
  const setPageIndex = useFilterStore((s) => s.setPageIndex);
  const activeTab = useListStore((s) => s.activeTab);
  const activeListId = useListStore((s) => s.activeListId);
  const setActiveList = useListStore((s) => s.setActiveList);
  const checkedIds = useSelectionStore((s) => s.checkedIds);
  const toggleOne = useSelectionStore((s) => s.toggleOne);
  const toggleAll = useSelectionStore((s) => s.toggleAll);
  const clearAll = useSelectionStore((s) => s.clearAll);
  const selectAllFilteredMode = useSelectionStore(
    (s) => s.selectAllFilteredMode,
  );
  const selectAllFilteredCount = useSelectionStore(
    (s) => s.selectAllFilteredCount,
  );
  const setSelectAllFiltered = useSelectionStore((s) => s.setSelectAllFiltered);

  const queryClient = useQueryClient();
  const isCreatingDefault = useRef(false);

  // Pagination state for selected/rejected tabs
  const [selectedPageIndex, setSelectedPageIndex] = useState(0);
  const [rejectedPageIndex, setRejectedPageIndex] = useState(0);

  // Fetch lists from server
  const { data: lists = [], isFetched: listsReady } = useQuery(
    INFLUENCER_QUERIES.lists(),
  );

  // Exclude "Rejected" list from selectable lists
  const selectableLists = lists.filter(
    (l) => l.name.toLowerCase() !== "rejected",
  );

  // Auto-create default list if none exist; always ensure a list is selected
  useEffect(() => {
    if (!listsReady) return;
    if (selectableLists.length === 0 && !isCreatingDefault.current) {
      isCreatingDefault.current = true;
      influencerAPIClient
        .createList("My First List")
        .then((newList) => {
          queryClient.invalidateQueries({ queryKey: INFLUENCER_KEYS.lists() });
          setActiveList(newList.id);
          isCreatingDefault.current = false;
        })
        .catch(() => {
          isCreatingDefault.current = false;
        });
    } else if (selectableLists.length > 0) {
      const activeExists = selectableLists.some((l) => l.id === activeListId);
      if (!activeListId || !activeExists) {
        const firstList = selectableLists[0];
        if (firstList) {
          setActiveList(firstList.id);
        }
      }
    }
  }, [listsReady, selectableLists, activeListId, setActiveList, queryClient]);

  // Paginated list members for selected tab display
  const selectedOffset = selectedPageIndex * SELECTED_PAGE_SIZE;
  const { data: selectedPageData, isLoading: isSelectedLoading } = useQuery({
    ...INFLUENCER_QUERIES.listMembersPage(
      activeListId ?? "",
      selectedOffset,
      SELECTED_PAGE_SIZE,
    ),
    enabled: !!activeListId && activeTab === "selected",
  });

  // Full list members (for discover tab's selectedIds check)
  const { data: membersData } = useQuery({
    ...INFLUENCER_QUERIES.listMembers(activeListId ?? ""),
    enabled: !!activeListId && activeTab === "discover",
  });

  // Find "Rejected" list and fetch its members for the rejected tab
  const rejectedList = lists.find((l) => l.name.toLowerCase() === "rejected");
  const rejectedOffset = rejectedPageIndex * SELECTED_PAGE_SIZE;
  const { data: rejectedPageData, isLoading: isRejectedLoading } = useQuery({
    ...INFLUENCER_QUERIES.listMembersPage(
      rejectedList?.id ?? "",
      rejectedOffset,
      SELECTED_PAGE_SIZE,
    ),
    enabled: !!rejectedList && activeTab === "rejected",
  });

  const selectedIds = useMemo(() => {
    if (activeTab === "discover") {
      return new Set((membersData?.members ?? []).map((m) => m.id));
    }
    // For selected tab, use paginated data for display
    return new Set((selectedPageData?.members ?? []).map((m) => m.id));
  }, [activeTab, membersData, selectedPageData]);

  const [profileInfluencer, setProfileInfluencer] = useState<Influencer | null>(
    null,
  );
  const [isProfileOpen, setIsProfileOpen] = useState(false);

  const handleRowClick = (influencer: Influencer) => {
    setProfileInfluencer(influencer);
    setIsProfileOpen(true);
  };

  const handleProfileOpenChange = (open: boolean) => {
    setIsProfileOpen(open);
  };

  // Reset page index when switching tabs
  useEffect(() => {
    setSelectedPageIndex(0);
    setRejectedPageIndex(0);
  }, [activeTab]);

  // Clear selection when tab or page changes
  useEffect(() => {
    clearAll();
  }, [
    activeTab,
    pagination.pageIndex,
    selectedPageIndex,
    rejectedPageIndex,
    clearAll,
  ]);

  // When notSelected is active, fetch 2x items to compensate for filtered-out members
  const discoverPagination = useMemo(
    () =>
      filters.notSelected
        ? { ...pagination, pageSize: pagination.pageSize * 2 }
        : pagination,
    [filters.notSelected, pagination],
  );

  // Discovery tab query
  const { data: discoverData, isLoading: isDiscoverLoading } = useQuery({
    ...INFLUENCER_QUERIES.search(filters, discoverPagination),
    enabled: activeTab === "discover",
  });

  // Derive table data and counts based on active tab
  let tableData: Influencer[];
  let totalCount: number;
  let totalPages: number;
  let showPagination = false;
  let currentPageIndex = 0;
  let handlePageChange = setPageIndex;

  if (activeTab === "selected") {
    tableData = selectedPageData?.members ?? [];
    totalCount =
      selectedPageData?.total ??
      lists.find((l) => l.id === activeListId)?.memberCount ??
      0;
    totalPages = Math.ceil(totalCount / SELECTED_PAGE_SIZE);
    showPagination = totalCount > SELECTED_PAGE_SIZE;
    currentPageIndex = selectedPageIndex;
    handlePageChange = setSelectedPageIndex;
  } else if (activeTab === "rejected") {
    tableData = rejectedPageData?.members ?? [];
    totalCount = rejectedPageData?.total ?? rejectedList?.memberCount ?? 0;
    totalPages = Math.ceil(totalCount / SELECTED_PAGE_SIZE);
    showPagination = totalCount > SELECTED_PAGE_SIZE;
    currentPageIndex = rejectedPageIndex;
    handlePageChange = setRejectedPageIndex;
  } else {
    const rawData = discoverData?.data ?? [];
    tableData = filters.notSelected
      ? rawData.filter((d) => !selectedIds.has(d.id))
      : rawData;
    totalCount = discoverData?.totalCount ?? 0;
    totalPages = Math.ceil(totalCount / discoverPagination.pageSize);
    showPagination = true;
    currentPageIndex = pagination.pageIndex;
    handlePageChange = setPageIndex;
  }

  const selectedCount =
    lists.find((l) => l.id === activeListId)?.memberCount ?? 0;
  const isLoading =
    (activeTab === "discover" && isDiscoverLoading && !discoverData) ||
    (activeTab === "selected" && isSelectedLoading && !selectedPageData) ||
    (activeTab === "rejected" && isRejectedLoading && !rejectedPageData);

  // Export all handler: fetches all members with progress callback
  const handleExportAll = useCallback(
    async (onProgress: (p: ExportProgress) => void): Promise<Influencer[]> => {
      const listId = activeTab === "rejected" ? rejectedList?.id : activeListId;
      if (!listId) return [];
      const result = await influencerAPIClient.getAllListMembers(
        listId,
        onProgress,
      );
      return result.members;
    },
    [activeTab, activeListId, rejectedList?.id],
  );

  // Select all banner for selected/rejected tabs
  const showSelectAllBanner =
    (activeTab === "selected" || activeTab === "rejected") &&
    !selectAllFilteredMode &&
    tableData.length > 0 &&
    tableData.every((d) => checkedIds.has(d.id)) &&
    totalCount > tableData.length;

  return (
    <div className="tw-flex tw-h-full tw-flex-col">
      <TabBar
        selectedCount={selectedCount}
        rejectedCount={rejectedList?.memberCount ?? 0}
      />

      {/* Header */}
      <div className="tw-flex tw-items-center tw-justify-between tw-px-8 tw-pt-4 tw-pb-4">
        <div>
          <h1 className="tw-text-lg tw-font-semibold tw-tracking-tight">
            Influencer Explorer
          </h1>
          <p className="tw-text-sm tw-text-muted-foreground">
            Browse and filter {platformHeaderLabel} creator profiles
          </p>
        </div>
        <div className="tw-flex tw-items-center tw-gap-3">
          <p className="tw-text-sm tw-text-muted-foreground">
            {totalCount.toLocaleString()} results
          </p>
          {activeTab === "discover" && <ImportDialog />}
          {activeTab !== "discover" && (
            <ExportButton
              influencers={
                checkedIds.size > 0
                  ? tableData.filter((d) => checkedIds.has(d.id))
                  : []
              }
              totalCount={totalCount}
              onExportAll={handleExportAll}
            />
          )}
        </div>
      </div>

      {/* Filters - only on discover tab */}
      {activeTab === "discover" && (
        <div className="tw-border-b tw-px-8 tw-py-3">
          <FilterBar />
        </div>
      )}

      {/* Bulk action bar + Select all banner */}
      {(activeTab === "discover" || activeTab === "selected") &&
        (checkedIds.size > 0 || selectAllFilteredMode) && (
          <div className="tw-px-8 tw-py-2 tw-space-y-2">
            <BulkActionBar
              checkedIds={checkedIds}
              onClear={clearAll}
              activeTab={activeTab}
              selectAllFilteredMode={selectAllFilteredMode}
              selectAllFilteredCount={selectAllFilteredCount}
              filters={filters}
            />
            {activeTab === "discover" &&
              !selectAllFilteredMode &&
              tableData.length > 0 &&
              tableData.every((d) => checkedIds.has(d.id)) &&
              totalCount > tableData.length && (
                <SelectAllBanner
                  pageCount={tableData.length}
                  totalCount={totalCount}
                  selectAllFilteredMode={false}
                  onSelectAll={() => setSelectAllFiltered(totalCount)}
                  onClear={clearAll}
                />
              )}
            {activeTab === "selected" && showSelectAllBanner && (
              <SelectAllBanner
                pageCount={tableData.length}
                totalCount={totalCount}
                selectAllFilteredMode={false}
                onSelectAll={() => setSelectAllFiltered(totalCount)}
                onClear={clearAll}
              />
            )}
            {selectAllFilteredMode && (
              <SelectAllBanner
                pageCount={tableData.length}
                totalCount={selectAllFilteredCount}
                selectAllFilteredMode={true}
                onSelectAll={() => setSelectAllFiltered(selectAllFilteredCount)}
                onClear={clearAll}
              />
            )}
          </div>
        )}

      {/* Table */}
      <div className="tw-flex-1 tw-overflow-auto tw-px-8 tw-py-4">
        {isLoading ? (
          <div className="tw-flex tw-items-center tw-justify-center tw-py-20">
            <div className="tw-text-sm tw-text-muted-foreground">Loading...</div>
          </div>
        ) : tableData.length === 0 && activeTab !== "discover" ? (
          <div className="tw-flex tw-items-center tw-justify-center tw-py-20">
            <div className="tw-text-sm tw-text-muted-foreground">
              No {activeTab} influencers yet.
            </div>
          </div>
        ) : (
          <InfluencerTable
            data={tableData}
            pageCount={totalPages}
            pageIndex={currentPageIndex}
            pageSize={
              activeTab === "discover"
                ? pagination.pageSize
                : SELECTED_PAGE_SIZE
            }
            onPageChange={handlePageChange}
            activeTab={activeTab}
            selectedIds={selectedIds}
            onRowClick={handleRowClick}
            checkedIds={checkedIds}
            onToggleOne={toggleOne}
            onToggleAll={toggleAll}
          />
        )}
      </div>

      {/* Pagination footer */}
      {showPagination && (
        <div className="tw-border-t tw-px-8">
          <InfluencerTablePagination
            pageIndex={currentPageIndex}
            pageSize={
              activeTab === "discover"
                ? pagination.pageSize
                : SELECTED_PAGE_SIZE
            }
            totalCount={totalCount}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </div>
      )}

      {/* Profile detail modal */}
      <InfluencerProfileModal
        influencer={profileInfluencer}
        open={isProfileOpen}
        onOpenChange={handleProfileOpenChange}
      />
    </div>
  );
}
